import {
  ProductFilter,
  ProductFilterSeasonsEnum,
  ProductFilterSortOrderEnum,
  SizeSizeTypeEnum,
  type Size,
} from '@trwl/backend.api';

/**
 * Десериализует query-параметры в объект фильтра (Omit<ProductFilter, 'page'>).
 * @param queryString строка query-параметров (может начинаться с '?')
 */
export function deserializeFilterFromQuery(
  queryString: string,
): Omit<ProductFilter, 'page'> {
  const params = new URLSearchParams(decodeURIComponent(queryString));

  const getNumberArray = (key: string): number[] =>
    params
      .getAll(key)
      .map(Number)
      .filter((v) => !isNaN(v));

  const getEnumArray = <T>(key: string, enumObj: Record<string, T>): T[] =>
    params
      .getAll(key)
      .filter((v) => Object.values(enumObj).includes(v as unknown as T)) as T[];

  const getBoolean = (key: string): boolean | undefined => {
    const v = params.get(key);
    if (v === 'true') return true;
    if (v === 'false') return false;
    return undefined;
  };

  const getNumber = (key: string): number | undefined => {
    const v = params.get(key);
    if (v === null) return undefined;
    const n = Number(v);
    return isNaN(n) ? undefined : n;
  };

  const getSizes = (): Size[] =>
    params
      .getAll('sizes')
      .map((s) => {
        const [sizeType, sizeValue] = s.split(':');
        if (
          sizeType &&
          sizeValue &&
          Object.values(SizeSizeTypeEnum).includes(sizeType as SizeSizeTypeEnum)
        ) {
          return { sizeType: sizeType as SizeSizeTypeEnum, sizeValue };
        }
        return null;
      })
      .filter(Boolean) as Size[];

  const filter: Omit<ProductFilter, 'page'> = {
    size: getNumber('size') ?? 40,
    keyword: params.get('keyword') ?? undefined,
    sortField: params.get('sortField') ?? undefined,
    sortOrder: params.get('sortOrder') as
      | ProductFilterSortOrderEnum
      | undefined,
    productIds: getNumberArray('productIds'),
    brandIds: getNumberArray('brandIds'),
    seriesIds: getNumberArray('seriesIds'),
    fitIds: getNumberArray('fitIds'),
    categoryIds: getNumberArray('categoryIds'),
    sizeIds: getNumberArray('sizeIds'),
    stock: getBoolean('stock'),
    color: params.get('color') ?? undefined,
    seasons: getEnumArray('seasons', ProductFilterSeasonsEnum),
    minPrice: getNumber('minPrice'),
    maxPrice: getNumber('maxPrice'),
    sizes: getSizes(),
  };

  return filter;
}

/**
 * Десериализует отдельное поле фильтра из query string.
 * @param key Ключ поля фильтра
 * @param queryString строка query-параметра (может начинаться с '?')
 * @returns Значение поля фильтра
 */
export function deserializeFilterFieldFromQuery<
  K extends keyof Omit<ProductFilter, 'page'>,
>(
  key: K,
  queryString: string,
  setPartialFilter: (filter: Partial<ProductFilter>) => void,
): any {
  switch (key) {
    case 'sizes': {
      setPartialFilter({
        sizes: queryString.split(',').map((size) => {
          const [sizeType, sizeValue] = size.split('_');
          return {
            sizeValue: sizeValue,
            sizeType: sizeType as SizeSizeTypeEnum,
          };
        }),
      });
      return;
    }
    case 'productIds':
    case 'brandIds':
    case 'seriesIds':
    case 'fitIds':
    case 'categoryIds':
    case 'sizeIds': {
      setPartialFilter({
        [key]: queryString
          .split(',')
          .map(Number)
          .filter((v) => !isNaN(v)),
      });
      return;
    }
    case 'seasons': {
      setPartialFilter({
        [key]: queryString.split(','),
      });
      return;
    }
    case 'size':
    case 'minPrice':
    case 'maxPrice':
      if (!isNaN(Number(queryString)))
        setPartialFilter({
          [key]: Number(queryString),
        });
      return;
    default:
      setPartialFilter({
        [key]: queryString,
      });
  }
}
